import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { detectPwaPlatform, isStandaloneMode } from '../utils/pwaPlatform';
import IOSInstallSheet from '../components/pwa/IOSInstallSheet';

const INSTALL_DISMISS_KEY = 'pwa-install-dismissed';
const INSTALL_DISMISS_DAYS = 7;

function isInstallBannerDismissed() {
  const dismissedAt = localStorage.getItem(INSTALL_DISMISS_KEY);
  if (!dismissedAt) return false;
  const elapsed = Date.now() - Number(dismissedAt);
  return elapsed < INSTALL_DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

let globalDeferredPrompt = null;
const promptSubscribers = new Set();

const notifyPromptSubscribers = () => {
  promptSubscribers.forEach((callback) => callback(globalDeferredPrompt));
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    if (isStandaloneMode()) return;
    globalDeferredPrompt = event;
    notifyPromptSubscribers();
  });

  window.addEventListener('appinstalled', () => {
    globalDeferredPrompt = null;
    notifyPromptSubscribers();
  });
}

const PWAInstallContext = createContext(null);

function waitForInstallPrompt(ms = 4000) {
  if (globalDeferredPrompt) return Promise.resolve(globalDeferredPrompt);

  return new Promise((resolve) => {
    const onPrompt = (event) => {
      event.preventDefault();
      if (!isStandaloneMode()) {
        globalDeferredPrompt = event;
        notifyPromptSubscribers();
      }
      cleanup();
      resolve(globalDeferredPrompt);
    };

    const timer = setTimeout(() => {
      cleanup();
      resolve(globalDeferredPrompt);
    }, ms);

    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', onPrompt);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
  });
}

function showBriefToast(message) {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById('pwa-install-toast');
  if (existing) existing.remove();

  const el = document.createElement('div');
  el.id = 'pwa-install-toast';
  el.setAttribute('role', 'status');
  el.textContent = message;
  Object.assign(el.style, {
    position: 'fixed',
    left: '50%',
    bottom: 'calc(5.5rem + env(safe-area-inset-bottom, 0px))',
    transform: 'translateX(-50%)',
    zIndex: '2147483000',
    maxWidth: '90vw',
    background: '#111827',
    color: '#fff',
    padding: '12px 16px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '600',
    lineHeight: '1.4',
    boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
    textAlign: 'center',
  });
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 4500);
}

export function PWAInstallProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(globalDeferredPrompt);
  const [isInstallable, setIsInstallable] = useState(Boolean(globalDeferredPrompt));
  const [isInstalled, setIsInstalled] = useState(isStandaloneMode);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [platform, setPlatform] = useState('desktop');
  const [isBannerDismissed, setIsBannerDismissed] = useState(isInstallBannerDismissed);
  const [isInstallPending, setIsInstallPending] = useState(false);
  const [showIOSInstall, setShowIOSInstall] = useState(false);

  useEffect(() => {
    setPlatform(detectPwaPlatform());
    setIsBannerDismissed(isInstallBannerDismissed());
    setIsInstalled(isStandaloneMode());
    setIsInstallable(Boolean(globalDeferredPrompt));
    setDeferredPrompt(globalDeferredPrompt);

    const handlePromptUpdate = (prompt) => {
      setDeferredPrompt(prompt);
      setIsInstallable(Boolean(prompt));
    };

    promptSubscribers.add(handlePromptUpdate);

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    const handleDisplayMode = () => setIsInstalled(isStandaloneMode());

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    const mql = window.matchMedia('(display-mode: standalone)');
    mql.addEventListener('change', handleDisplayMode);

    return () => {
      promptSubscribers.delete(handlePromptUpdate);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      mql.removeEventListener('change', handleDisplayMode);
    };
  }, []);

  const installApp = useCallback(async () => {
    const promptEvent = globalDeferredPrompt || deferredPrompt;
    if (!promptEvent) return false;

    try {
      await promptEvent.prompt();
      const { outcome } = await promptEvent.userChoice;
      globalDeferredPrompt = null;
      setDeferredPrompt(null);
      setIsInstallable(false);
      notifyPromptSubscribers();

      if (outcome === 'accepted') {
        setIsInstalled(true);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error invoking PWA install prompt:', err);
      return false;
    }
  }, [deferredPrompt]);

  /**
   * - iPhone → iOS install sheet (Add to Home Screen)
   * - Android / Desktop → native Chrome/Edge install dialog (direct download)
   */
  const requestInstall = useCallback(async () => {
    if (isStandaloneMode()) {
      setIsInstalled(true);
      return 'installed';
    }

    if (detectPwaPlatform() === 'ios') {
      setShowIOSInstall(true);
      return 'ios-ui';
    }

    setIsInstallPending(true);
    try {
      let promptEvent = globalDeferredPrompt || deferredPrompt;
      if (!promptEvent) {
        promptEvent = await waitForInstallPrompt(4000);
        if (promptEvent) {
          setDeferredPrompt(promptEvent);
          setIsInstallable(true);
        }
      }

      if (promptEvent || globalDeferredPrompt) {
        const ok = await installApp();
        if (!ok) {
          showBriefToast('Install cancelled. Tap Install App again to retry.');
        }
        return ok ? 'installed' : 'dismissed';
      }

      showBriefToast('Use Chrome or Edge, then tap Install App for direct download.');
      return 'unavailable';
    } finally {
      setIsInstallPending(false);
    }
  }, [deferredPrompt, installApp]);

  const closeIOSInstall = useCallback(() => {
    setShowIOSInstall(false);
  }, []);

  const dismissInstallBanner = useCallback(() => {
    localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now()));
    setIsBannerDismissed(true);
  }, []);

  const canInstall = isInstallable && !isInstalled;
  const shouldShowInstallPromo = !isInstalled && !isBannerDismissed;

  const value = useMemo(
    () => ({
      platform,
      isInstallable,
      isInstalled,
      isOnline,
      isBannerDismissed,
      isInstallPending,
      showIOSInstall,
      installApp,
      requestInstall,
      closeIOSInstall,
      dismissInstallBanner,
      canInstall,
      shouldShowInstallPromo,
      showInstallGuide: showIOSInstall,
      closeInstallGuide: closeIOSInstall,
      openInstallGuide: () => setShowIOSInstall(true),
    }),
    [
      platform,
      isInstallable,
      isInstalled,
      isOnline,
      isBannerDismissed,
      isInstallPending,
      showIOSInstall,
      installApp,
      requestInstall,
      closeIOSInstall,
      dismissInstallBanner,
      canInstall,
      shouldShowInstallPromo,
    ]
  );

  return (
    <PWAInstallContext.Provider value={value}>
      {children}
      <IOSInstallSheet open={showIOSInstall} onClose={closeIOSInstall} />
    </PWAInstallContext.Provider>
  );
}

export function usePWA() {
  const ctx = useContext(PWAInstallContext);
  if (!ctx) {
    throw new Error('usePWA must be used within PWAInstallProvider');
  }
  return ctx;
}
